import { PortableText as PortableTextSanity, PortableTextBlock } from '@portabletext/react';
import Typography from '@mui/material/Typography';
import ImagePortable from './ImagePortable';
import YouTubeEmbed from './YouTubeEmbed';

import Link from '@/components/Link';
import Box from '@mui/material/Box';

export default function PortableText({
  blocks,
  smallList = false,
}: {
  blocks: PortableTextBlock[];
  smallList?: boolean;
}) {
  return (
    <PortableTextSanity
      value={blocks}
      components={{
        types: {
          image: ({ value }) => <ImagePortable value={value} />,
          youtube: ({ value }) => <YouTubeEmbed videoUrl={value.url} />,
        },
        marks: {
          link: ({ children, value }) => (
            <Link
              href={value.href}
              target={value.blank ? '_blank' : '_self'}
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.dark',
                },
              }}
            >
              {children}
            </Link>
          ),
        },
        block: {
          h1: ({ children }) => (
            <Typography
              variant='h1'
              id={`${children}`}
              sx={{ my: 2, fontWeight: 700, color: 'secondary.main', fontSize: '3.5rem' }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography
              variant='h2'
              id={`${children}`}
              sx={{ my: 2, fontWeight: 700, color: 'secondary.main', fontSize: '2rem' }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography
              variant='h3'
              id={`${children}`}
              sx={{ my: 1.5, fontWeight: 700, color: 'primary.main', fontSize: '1.75rem' }}
            >
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography
              variant='h4'
              sx={{ my: 1.5, fontWeight: 700, fontSize: '1.5rem' }}
              id={`${children}`}
            >
              {children}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography
              variant='h5'
              sx={{ my: 1, fontWeight: 700, fontSize: '1.25rem' }}
              id={`${children}`}
            >
              {children}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography
              variant='h6'
              sx={{ my: 1, fontWeight: 700, fontSize: '1.1rem' }}
              id={`${children}`}
              color='primary.main'
            >
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography
              variant='body1'
              sx={{
                mb: 2,
                lineHeight: 1.7,
                color: 'text.primary',
                fontSize: '1rem',
              }}
            >
              {children}
            </Typography>
          ),
          blockquote: ({ children }: any) => (
            <Box
              sx={{
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                pl: 3,
                py: 1,
                my: 2,
                mx: 2,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography
                variant='body1'
                component='blockquote'
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  m: 0,
                  fontSize: '1.1rem',
                }}
              >
                {children}
              </Typography>
            </Box>
          ),
        },
        list: {
          bullet: ({ children }: any) => (
            <Box
              component='ul'
              sx={{
                pl: 2,
                pr: 2,
                mb: 2,
                '& li': {
                  mb: !smallList ? 1 : 0,
                  '&::marker': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              {children}
            </Box>
          ),
          number: ({ children }: any) => (
            <Box
              component='ol'
              sx={{
                pl: 2,
                pr: 2,
                my: 1,
                '& li': {
                  mb: 1,
                  '&::marker': {
                    color: 'primary.main',
                    fontWeight: 500,
                  },
                },
              }}
            >
              {children}
            </Box>
          ),
        },
        listItem: {
          bullet: ({ children }: any) => (
            <Typography
              component='li'
              variant='body1'
              sx={{ color: 'text.primary', fontSize: '1rem' }}
            >
              {children}
            </Typography>
          ),
          number: ({ children }: any) => (
            <Typography
              component='li'
              variant='body1'
              sx={{ color: 'text.primary', fontSize: '1rem' }}
            >
              {children}
            </Typography>
          ),
        },
      }}
    />
  );
}
